import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { Select } from "@/components/ui/select";
import { plural } from "@/lib/plural";

/**
 * The section the lines move to. One offered when the panel opened may be deleted while it is
 * open: the choice falls to one still there, so a move never names a section that is gone.
 */
function useMoveTarget(others: { id: string }[]) {
  const [picked, setPicked] = useState(others[0]?.id ?? "");
  const target = others.some((section) => section.id === picked)
    ? picked
    : (others[0]?.id ?? "");
  return [target, setPicked] as const;
}

function holds(subsections: number, figures: number): string {
  return figures === 0
    ? `It holds ${plural(subsections, "subsection")}.`
    : `It holds ${plural(subsections, "subsection")}, including ${plural(figures, "figure")}.`;
}

/** Opened by a press, the question takes focus once, as it appears: it is answered there. */
function takeFocus(panel: HTMLDivElement | null) {
  panel?.querySelector<HTMLElement>("select, button")?.focus();
}

/**
 * The question a section with subsections asks before it goes: move them to another section,
 * or delete them with it. Praxis's items deleted with it go to Removed; the attorney's go for good.
 */
export function DeleteSectionPanel({
  title,
  subsections,
  figures,
  others,
  onMove,
  onDeleteAll,
  onCancel,
}: {
  title: string;
  subsections: number;
  figures: number;
  others: { id: string; title: string }[];
  onMove: (sectionId: string) => void;
  onDeleteAll: () => void;
  onCancel: () => void;
}) {
  const [target, setPicked] = useMoveTarget(others);
  const questionId = useId();
  const reasonId = useId();
  return (
    <Panel
      tone="warning"
      pad="snug"
      role="alertdialog"
      aria-labelledby={questionId}
      aria-describedby={reasonId}
      className="mt-2 space-y-2 text-sm"
      ref={takeFocus}
    >
      <p id={questionId} className="font-semibold">
        {`Delete "${title}"? ${holds(subsections, figures)}`}
      </p>
      <p id={reasonId}>
        Move them to another section, or delete them with the section.
        {figures > 0 &&
          " Deleted figures go to Removed and show as not placed until you reinstate them."}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {others.length > 0 && (
          <>
            <Select
              size="dense"
              aria-label="Move them to"
              value={target}
              className="w-auto"
              onChange={(event) => {
                setPicked(event.target.value);
              }}
            >
              {others.map((section) => (
                <option key={section.id} value={section.id}>
                  {section.title}
                </option>
              ))}
            </Select>
            <Button
              variant="primary"
              size="dense"
              onClick={() => {
                onMove(target);
              }}
            >
              Move and delete section
            </Button>
          </>
        )}
        <Button variant="danger" size="dense" onClick={onDeleteAll}>
          Delete section and subsections
        </Button>
        <Button variant="tertiary" size="dense" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Panel>
  );
}
